import { create } from "zustand";
import { distributorsApi, type DistributorBranding } from "@/lib/api/distributors";

/**
 * Distributor branding branding.
 *
 * Applies a distributor's identity (colors, logo, favicon, product name) over
 * the default EverythingCloud theme. Colors are injected as inline custom
 * properties on `<html>` — inline declarations beat the stylesheet's
 * `:root`/`.dark`/`.dark-black`/`.dark-glass`/`.dark-teal` rules, so one
 * override rebrands every theme mode and cascades through all
 * `hsl(var(--token))` consumers automatically. For surface colors we also
 * derive a contrasting `*-foreground` so text stays legible on any brand color.
 *
 * This is why the `.dark-teal` default theme derives every accent from
 * `--primary` rather than restating the teal literal: the ambient washes, the
 * masked grid, focus rings and the login headline gradient all recolour with a
 * distributor's brand instead of stranding it on a teal canvas. Structural
 * tokens (`--background`, `--page-background`, `--card`, `--border`, `--muted*`,
 * `--popover*`, `--radius`) are deliberately NOT in `VAR_NAME` below, so the
 * canvas stays theme-owned and a brand colour can never make the app
 * unreadable.
 *
 * A distributor or an MSP may brand, and the nearest branded organization wins:
 * an MSP's branding beats its distributor's for everyone beneath the MSP. The
 * backend resolves which organization brands the current user.
 *
 * Persisted to localStorage so a hard refresh paints the branded shell before
 * React mounts (see `bootstrapBranding`), and the login screen can rebrand for a
 * returning user from local state alone — no unauthenticated endpoint to scrape.
 */

// token key (snake) → CSS custom property name
const VAR_NAME: Record<string, string> = {
  primary: "--primary",
  accent: "--accent",
  secondary: "--secondary",
  ring: "--ring",
  sidebar: "--sidebar",
  sidebar_accent: "--sidebar-accent",
  sidebar_foreground: "--sidebar-foreground",
  chart_1: "--chart-1",
  chart_2: "--chart-2",
  chart_3: "--chart-3",
  chart_4: "--chart-4",
  chart_5: "--chart-5",
  success: "--success",
  warning: "--warning",
  info: "--info",
};

// Surface tokens that need a readable text color derived on top of them.
const FOREGROUND_VAR: Record<string, string> = {
  primary: "--primary-foreground",
  accent: "--accent-foreground",
  secondary: "--secondary-foreground",
  sidebar: "--sidebar-foreground",
};

interface BrandingState {
  enabled: boolean;
  productName: string | null;
  logo: string | null;
  logoDark: string | null;
  favicon: string | null;
  colors: Record<string, string>;
  loaded: boolean;
  fetchAndApply: () => Promise<void>;
  reset: () => void;
}

/** Parse `#rrggbb` → [r,g,b] 0-255, or null. */
function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/** Convert `#1976d2` → `"210 79% 46%"` (the HSL triplet our CSS tokens expect). */
export function hexToHslTriplet(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Pick a readable foreground (near-black or near-white) for a background hex. */
function contrastForeground(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  // WCAG relative luminance.
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45 ? "222 47% 11%" : "0 0% 100%"; // dark text on light, white on dark
}

const INJECTED_VARS = [
  ...Object.values(VAR_NAME),
  ...Object.values(FOREGROUND_VAR),
];

function clearColors() {
  const root = document.documentElement;
  INJECTED_VARS.forEach((v) => root.style.removeProperty(v));
}

function applyColors(colors: Record<string, string>) {
  const root = document.documentElement;
  for (const [key, hex] of Object.entries(colors)) {
    const varName = VAR_NAME[key];
    if (!varName) continue;
    const triplet = hexToHslTriplet(hex);
    if (!triplet) continue;
    root.style.setProperty(varName, triplet);
    // Derive a contrasting foreground for surface tokens, unless the caller
    // supplied an explicit foreground token (e.g. sidebar_foreground).
    const fgVar = FOREGROUND_VAR[key];
    if (fgVar && !(key === "sidebar" && colors.sidebar_foreground)) {
      const fg = contrastForeground(hex);
      if (fg) root.style.setProperty(fgVar, fg);
    }
  }
}

// Title + favicon — saved once so reset() can restore them.
let originalTitle: string | null = null;
let originalFavicon: string | null = null;

function applyFavicon(href: string | null) {
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!link) return;
  if (originalFavicon === null) originalFavicon = link.getAttribute("href");
  if (href) link.setAttribute("href", href);
}

function applyTitle(name: string | null) {
  if (originalTitle === null) originalTitle = document.title;
  if (name) document.title = name;
}

function restoreChrome() {
  if (originalFavicon !== null) {
    const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    link?.setAttribute("href", originalFavicon);
  }
  if (originalTitle !== null) document.title = originalTitle;
}

const EMPTY = {
  enabled: false,
  productName: null as string | null,
  logo: null as string | null,
  logoDark: null as string | null,
  favicon: null as string | null,
  colors: {} as Record<string, string>,
};

// ---- Persistence ------------------------------------------------------------
// Branding is persisted so (1) a hard refresh paints the branded shell before
// React mounts (no flash) and (2) the login screen can rebrand for a returning
// user from local state alone — no unauthenticated network call, no endpoint to
// enumerate. Last authenticated session wins; a session that resolves to "no
// branding" clears it.

const STORAGE_KEY = "everythingcloud-branding";

type PersistedBranding = Omit<typeof EMPTY, "colors"> & { colors: Record<string, string> };

function readPersisted(): PersistedBranding | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<PersistedBranding>;
    if (!p || !p.enabled) return null;
    return {
      enabled: true,
      productName: p.productName ?? null,
      logo: p.logo ?? null,
      logoDark: p.logoDark ?? null,
      favicon: p.favicon ?? null,
      colors: p.colors ?? {},
    };
  } catch {
    return null;
  }
}

function writePersisted(state: PersistedBranding) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage full / unavailable — branding still works for this session */
  }
}

function clearPersisted() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Apply persisted branding synchronously, before React mounts. Call from
 * `main.tsx` (mirrors the theme bootstrap) so the branded colors, favicon, and
 * tab title are present on first paint — no flash of the default theme.
 */
export function bootstrapBranding(): void {
  const p = readPersisted();
  if (!p) return;
  applyColors(p.colors);
  applyFavicon(p.favicon);
  applyTitle(p.productName);
}

// Seed store state from persisted branding so logo/name render branded on the
// very first commit (the colors/favicon/title are already applied by bootstrap).
const INITIAL = { ...EMPTY, ...(readPersisted() ?? {}) };

/** The newest `fetchAndApply` call; an older answer is dropped. */
let latestFetch = 0;

export const useBrandingStore = create<BrandingState>()((set) => {
  const apply = (b: DistributorBranding) => {
    if (!b.enable_branding) {
      clearColors();
      restoreChrome();
      clearPersisted();
      set({ ...EMPTY, loaded: true });
      return;
    }
    const next = {
      enabled: true as const,
      productName: b.product_name ?? b.name ?? null,
      logo: b.logo ?? null,
      logoDark: b.logo_dark ?? b.logo ?? null,
      favicon: b.favicon ?? null,
      colors: b.colors ?? {},
    };
    applyColors(next.colors);
    applyFavicon(next.favicon);
    applyTitle(next.productName);
    writePersisted(next);
    set({ ...next, loaded: true });
  };

  return {
    ...INITIAL,
    loaded: false,

    fetchAndApply: async () => {
      // A switch of identity can overlap two fetches. Only the newest may
      // paint, or a slow answer for the previous person shows their brand.
      const ticket = ++latestFetch;
      try {
        const branding = await distributorsApi.getBranding();
        if (ticket === latestFetch) apply(branding);
      } catch {
        if (ticket === latestFetch) set({ loaded: true }); // best-effort; never block the app
      }
    },

    reset: () => {
      clearColors();
      restoreChrome();
      clearPersisted();
      set({ ...EMPTY, loaded: false });
    },
  };
});
